import { useT } from "@/lib/i18n";

export type Office = {
  slug: string;
  countryCode: "ES" | "PT" | "BR";
  city: string;
  postalCode: string;
  address: string;
  mapUrl: string;
  isHeadquarters: boolean;
  name: string;
};

export type OfficeGroup = {
  countryCode: "ES" | "PT" | "BR";
  country: string;
  offices: Office[];
};

/**
 * PULSO33 operates as a remote-first distributed team. Locations listed here
 * are reachable hubs where team members work. Backoffice can override every
 * label via i18n keys.
 */
export function useOffices(): OfficeGroup[] {
  const { tk } = useT();

  const offices: Office[] = [
    {
      slug: "es-hq",
      countryCode: "ES",
      city: tk("offices.es-hq.city", "Spain"),
      postalCode: "",
      address: tk("offices.es-hq.address", "Distributed engineering hub"),
      mapUrl: "https://pulso33.com",
      isHeadquarters: true,
      name: tk("offices.es-hq.name", "PULSO33 — Spain"),
    },
    {
      slug: "pt-hq",
      countryCode: "PT",
      city: tk("offices.pt-hq.city", "Portugal"),
      postalCode: "",
      address: tk("offices.pt-hq.address", "Remote engineering team"),
      mapUrl: "https://pulso33.com",
      isHeadquarters: false,
      name: tk("offices.pt-hq.name", "PULSO33 — Portugal"),
    },
    {
      slug: "br-hq",
      countryCode: "BR",
      city: tk("offices.br-hq.city", "Brazil"),
      postalCode: "",
      address: tk("offices.br-hq.address", "Remote engineering team"),
      mapUrl: "https://pulso33.com",
      isHeadquarters: false,
      name: tk("offices.br-hq.name", "PULSO33 — Brazil"),
    },
  ];

  const countryLabel: Record<Office["countryCode"], string> = {
    ES: tk("offices.country.ES", "Spain"),
    PT: tk("offices.country.PT", "Portugal"),
    BR: tk("offices.country.BR", "Brazil"),
  };

  const order: Office["countryCode"][] = ["ES", "PT", "BR"];
  return order.map((code) => ({
    countryCode: code,
    country: countryLabel[code],
    offices: offices.filter((o) => o.countryCode === code),
  }));
}
